/**
 * Employment Hero workforce figures, all derived from world.ts STAFF:
 * headcount, hires/leavers, turnover (12-month rolling), SCHADS-band
 * attrition, ATSI share, sick/other leave, fortnightly payroll and the
 * recruitment pipeline. Every count reconciles with the roster, and payroll
 * $ reconcile to the finance ledger's wage and super accounts.
 */
import { Quarter, quarterOf, ymd } from '../../clock';
import { rng } from '../../rng';
import { lastSyncedAt } from '../../state';
import { ORG, PROGRAMS, SITES, STAFF, Staff, leaverHistoryStart } from '../../world';
import {
    BandKey,
    ByType,
    DATA_START_DAY,
    TYPE_KEYS,
    TypeKey,
    activeOn,
    activeToday,
    bandOf,
    daily,
    daysBetween,
    ehId,
    ehTypeLabel,
    emptyByType,
    isActiveOn,
    isFrontline,
    isRemote,
    lastDayOfMonth,
    levelNum,
    monthKeys,
    parseDay,
    payPoint,
    pct1,
    round1,
    round2,
    shiftDay,
    shiftMonths,
    standardHours,
    tenureDays,
    today,
    trendWindow,
    typeOf,
    typicalHours,
} from './common';
import { SUPER_ACCOUNTS, WAGE_ACCOUNTS, ledgerWages, monthActuals } from '../finance/ledger';
import { complianceByCategory, complianceSummary } from './compliance';
import { trainingSummary } from './training';

const countBy = <T>(xs: T[], f: (x: T) => boolean): number => xs.reduce((n, x) => n + (f(x) ? 1 : 0), 0);

function byType(xs: Staff[]): ByType<number> {
    const out = emptyByType();
    for (const s of xs) {
        out[typeOf(s)]++;
    }
    return out;
}

/**
 * THE starter/leaver rule, used by every count on every surface (tiles,
 * monthly tables, trends, CEO report, assistant): the start/end date falls
 * within [from, to] AND is on or before today. A staff member whose last day
 * is d is still active on d-1 and counted as a leaver on d.
 */
const capToday = (to: string): string => (to < today() ? to : today());
const startedBetween = (from: string, to: string): Staff[] => {
    const end = capToday(to);
    return STAFF.filter(s => s.startDate >= from && s.startDate <= end);
};
const endedBetween = (from: string, to: string): Staff[] => {
    const end = capToday(to);
    return STAFF.filter(s => !!s.endDate && s.endDate >= from && s.endDate <= end);
};
const startedInMonth = (m: string): Staff[] => startedBetween(`${m}-01`, lastDayOfMonth(m));
const endedInMonth = (m: string): Staff[] => endedBetween(`${m}-01`, lastDayOfMonth(m));

// ---------------------------------------------------------------------------
// Employees
// ---------------------------------------------------------------------------

function employeeRow(s: Staff) {
    const level = levelNum(s);
    const pp = payPoint(s);
    return {
        id: ehId(s),
        name: s.name,
        email: s.email,
        status: isActiveOn(s, today()) ? 'active' : s.startDate > today() ? 'pending' : 'terminated',
        start_date: s.startDate,
        termination_date: s.endDate,
        job_title: s.position,
        department: s.program,
        location: s.site,
        employment_type: ehTypeLabel(s),
        employment_type_bucket: typeOf(s),
        teams: [s.site],
        schads_classification: `SCHADS Level ${level} Pay Point ${pp}`,
        schads_level: level,
        schads_band: bandOf(s),
        pay_rate: s.hourlyRate,
        pay_rate_unit: 'Hourly',
        standard_hours_per_week: standardHours(s),
    };
}

function employeesBlock() {
    const active = activeToday();
    const terminated = STAFF.filter(s => !!s.endDate && s.endDate <= today());
    return {
        total_active: active.length,
        total_terminated: terminated.length,
        total: STAFF.length,
        by_type: byType(active),
        list: [...STAFF].sort((a, b) => a.name.localeCompare(b.name)).map(employeeRow),
    };
}

// ---------------------------------------------------------------------------
// Turnover
// ---------------------------------------------------------------------------

/** A workforce segment for turnover: a stable key (for memoising) and a membership test. */
interface Segment {
    key: string;
    test: (s: Staff) => boolean;
}

const ALL_STAFF: Segment = { key: 'all', test: () => true };
const typeSegment = (type: TypeKey): Segment => ({ key: `type:${type}`, test: s => typeOf(s) === type });
const bandSegment = (band: BandKey): Segment => ({ key: `band:${band}`, test: s => bandOf(s) === band });

const programSegment = (program: string): Segment => ({ key: `program:${program}`, test: s => s.program === program });

/**
 * First day of the daily headcount series: a year before leaver history
 * begins, so the average headcount of every trailing-12-month window (back to
 * the first selectable quarter and the CEO report's look-back before it) is
 * computed from the roster over the whole window.
 */
const HC_BASE = shiftDay(ymd(leaverHistoryStart()), -365);

/**
 * Prefix sums of a segment's daily active headcount from HC_BASE to today
 * (prefix[i] = sum of headcounts on days 0..i-1), so the average headcount
 * over any window is O(1).
 */
function headcountPrefix(seg: Segment): number[] {
    return daily(`people:hc:${seg.key}`, () => {
        const n = daysBetween(HC_BASE, today()) + 1;
        const diff = new Array<number>(n + 1).fill(0);
        for (const s of STAFF) {
            if (!seg.test(s)) {
                continue;
            }
            const a = Math.max(0, daysBetween(HC_BASE, s.startDate));
            const b = s.endDate ? Math.min(n, daysBetween(HC_BASE, s.endDate)) : n; // active while day < endDate
            if (a < b) {
                diff[a]++;
                diff[b]--;
            }
        }
        const prefix = new Array<number>(n + 1).fill(0);
        let run = 0;
        for (let i = 0; i < n; i++) {
            run += diff[i];
            prefix[i + 1] = prefix[i] + run;
        }
        return prefix;
    });
}

/** Mean daily headcount of a segment over [from, to] (both inclusive, to <= today), 1 dp. */
function avgHeadcount(seg: Segment, from: string, to: string): number {
    const prefix = headcountPrefix(seg);
    const i = Math.max(0, daysBetween(HC_BASE, from));
    const j = Math.min(prefix.length - 2, daysBetween(HC_BASE, to));
    return j >= i ? round1((prefix[j + 1] - prefix[i]) / (j - i + 1)) : 0;
}

interface TurnoverFigures {
    leavers: number;
    avg_headcount: number;
    rate: number;
}

/**
 * 12-month rolling turnover as at `day` for a segment: leavers in the 365
 * days to `day` ÷ average daily headcount over the same 365 days. The rate is
 * computed from the 1-dp average that is displayed beside it, so
 * leavers ÷ avg headcount on the page reproduces it exactly. Every split
 * (employment type, SCHADS band, program) uses this definition, so leavers
 * and average headcounts add up across a split and the total rate always
 * sits between the parts' rates. The window always reaches back the full
 * 365 days: leaver history and the headcount series start well before the
 * Dec 2025 data window, so a past quarter's figure is a true 12-month rate,
 * directly comparable with today's.
 */
function segmentTurnover(day: string, seg: Segment): TurnoverFigures {
    const from = shiftDay(day, -364);
    const leavers = endedBetween(from, day).filter(seg.test).length;
    const avg = avgHeadcount(seg, from, day);
    return { leavers, avg_headcount: avg, rate: avg > 0 ? round1((leavers / avg) * 100) : 0 };
}

/** 12-month rolling turnover % (1 dp) as at `day`, optionally for one employment type. */
export function turnoverRate12m(day: string, type?: TypeKey): number {
    return segmentTurnover(day, type ? typeSegment(type) : ALL_STAFF).rate;
}

/**
 * 12-month rolling turnover as at `day` with the counts behind it, for any
 * surface that quotes the rate (CEO report, assistant): `rate` is exactly
 * `leavers` ÷ `avg_headcount`, and as at today it equals the HR page tile.
 */
export function turnover12m(day: string): TurnoverFigures & { from: string; to: string } {
    const to = capToday(day);
    return { ...segmentTurnover(to, ALL_STAFF), from: shiftDay(to, -364), to };
}

function turnoverBlock() {
    const t = today();
    const from = shiftDay(t, -364);
    const active = activeToday();
    const terminated = endedBetween(from, t);
    const hired = startedBetween(from, t);
    const total = segmentTurnover(t, ALL_STAFF);
    const rateByType = emptyByType();
    const avgByType = emptyByType();
    for (const k of ['fulltime', 'parttime', 'casual'] as TypeKey[]) {
        const f = segmentTurnover(t, typeSegment(k));
        rateByType[k] = f.rate;
        avgByType[k] = f.avg_headcount;
    }
    return {
        definition: 'Leavers in the 12 months to today ÷ average daily headcount over the same 12 months. All staff incl. casuals.',
        window: { from, to: t },
        turnover_rate_12m: total.rate,
        turnover_rate_12m_by_type: rateByType,
        avg_headcount_12m: total.avg_headcount,
        avg_headcount_12m_by_type: avgByType,
        terminated_12m: terminated.length,
        terminated_12m_by_type: byType(terminated),
        hired_12m: hired.length,
        hired_12m_by_type: byType(hired),
        active_headcount: active.length,
        active_headcount_by_type: byType(active),
        monthly_trend: monthKeys().map(m => {
            const h = startedInMonth(m);
            const x = endedInMonth(m);
            return { month: m, hires: h.length, terminations: x.length, hires_by_type: byType(h), terminations_by_type: byType(x) };
        }),
        by_reason: Object.entries(
            terminated.reduce<Record<string, number>>((acc, s) => {
                const k = s.terminationReason || 'Not recorded';
                acc[k] = (acc[k] || 0) + 1;
                return acc;
            }, {}),
        )
            .map(([reason, count]) => ({ reason, count }))
            .sort((a, b) => b.count - a.count),
    };
}

// ---------------------------------------------------------------------------
// SCHADS-band attrition — same basis as the headline turnover (all staff,
// casuals included), so DSW and Corporate always bracket the total.
// ---------------------------------------------------------------------------

function bandCounts(xs: Staff[]): Record<BandKey, number> {
    const out = { dsw: 0, corporate: 0, unknown: 0 };
    for (const s of xs) {
        out[bandOf(s)]++;
    }
    return out;
}

function attritionBlock() {
    const t = today();
    const active = activeToday();
    const headcount = bandCounts(active);
    const terminated = bandCounts(endedBetween(shiftDay(t, -364), t));
    const fig = (k: BandKey) => segmentTurnover(t, bandSegment(k));
    const dsw = fig('dsw');
    const corporate = fig('corporate');
    const unknown = fig('unknown');
    return {
        definition: 'DSW = SCHADS Level ≤ 3; Corporate/Mgmt/Clinical = Level ≥ 4. Leavers in the last 12 months ÷ average headcount over those 12 months, all staff incl. casuals — same basis as the headline turnover.',
        terminated_12m_by_band: terminated,
        active_headcount_by_band: headcount,
        avg_headcount_12m_by_band: { dsw: dsw.avg_headcount, corporate: corporate.avg_headcount, unknown: unknown.avg_headcount },
        turnover_rate_12m_by_band: { dsw: dsw.rate, corporate: corporate.rate, unknown: unknown.rate },
        turnover_rate_12m_total: turnoverRate12m(t),
        excluded_casual: 0,
        excluded_short_tenure: 0,
        unclassified_active: countBy(active, s => bandOf(s) === 'unknown'),
        monthly_trend: monthKeys().map(m => {
            const end = lastDayOfMonth(m) < t ? lastDayOfMonth(m) : t;
            const hc = bandCounts(activeOn(end));
            const x = bandCounts(endedInMonth(m));
            return {
                month: m,
                dsw: x.dsw,
                corporate: x.corporate,
                unknown: x.unknown,
                dsw_rate: hc.dsw ? pct1(x.dsw, hc.dsw) : null,
                corporate_rate: hc.corporate ? pct1(x.corporate, hc.corporate) : null,
            };
        }),
    };
}

// ---------------------------------------------------------------------------
// Workforce composition: FTE, classification, program/site, tenure, ATSI
// ---------------------------------------------------------------------------

const fteOf = (s: Staff) => round2((standardHours(s) ?? 0) / 38);

function fteBlock() {
    const active = activeToday();
    const byDivision: Record<string, number> = {};
    const byBand = { dsw: 0, corporate: 0, unknown: 0 };
    for (const s of active) {
        byDivision[s.program] = round2((byDivision[s.program] || 0) + fteOf(s));
        byBand[bandOf(s)] = round2(byBand[bandOf(s)] + fteOf(s));
    }
    return {
        full_time_equivalent_hours: 38,
        total_fte: round1(active.reduce((n, s) => n + fteOf(s), 0)),
        by_division: byDivision,
        by_band: byBand,
        employees_without_standard_hours: active.filter(s => standardHours(s) === null).map(s => s.name).sort(),
    };
}

function classificationBlock() {
    const active = activeToday();
    const t = today();
    const byLevel: Record<string, { count: number; band: BandKey; avg_hourly_rate: number; min_hourly_rate: number; max_hourly_rate: number; terminated_12m: number }> = {};
    const levels = [...new Set(STAFF.map(s => s.level))].sort((a, b) => levelNum({ level: a } as Staff) - levelNum({ level: b } as Staff));
    const leavers = endedBetween(shiftDay(t, -364), t);
    for (const level of levels) {
        const xs = active.filter(s => s.level === level);
        const rates = xs.map(s => s.hourlyRate);
        byLevel[level] = {
            count: xs.length,
            band: bandOf({ level } as Staff),
            avg_hourly_rate: rates.length ? round2(rates.reduce((a, b) => a + b, 0) / rates.length) : 0,
            min_hourly_rate: rates.length ? Math.min(...rates) : 0,
            max_hourly_rate: rates.length ? Math.max(...rates) : 0,
            terminated_12m: countBy(leavers, s => s.level === level),
        };
    }
    return { by_level: byLevel, by_band: bandCounts(active) };
}

function workforceBlock() {
    const active = activeToday();
    const t = today();
    const from = shiftDay(t, -364);
    const leavers = endedBetween(from, t);
    const hires = startedBetween(from, t);
    const avg = (xs: number[]) => (xs.length ? round2(xs.reduce((a, b) => a + b, 0) / xs.length) : 0);
    const years = active.map(s => tenureDays(s) / 365).sort((a, b) => a - b);
    const bucket = (lo: number, hi: number) => countBy(years, y => y >= lo && y < hi);
    return {
        by_program: PROGRAMS.map(program => {
            const xs = active.filter(s => s.program === program);
            const left = countBy(leavers, s => s.program === program);
            const avgHc = avgHeadcount(programSegment(program), from, t);
            return {
                program,
                active: xs.length,
                fte: round1(xs.reduce((n, s) => n + fteOf(s), 0)),
                by_type: byType(xs),
                hired_12m: countBy(hires, s => s.program === program),
                terminated_12m: left,
                avg_headcount_12m: avgHc,
                turnover_rate_12m: pct1(left, avgHc),
                avg_hourly_rate: avg(xs.map(s => s.hourlyRate)),
                atsi_percent: pct1(countBy(xs, s => s.aboriginal), xs.length),
            };
        }),
        by_site: SITES.map(site => {
            const xs = active.filter(s => s.site === site.name);
            return { site: site.name, region: site.region, active: xs.length, terminated_12m: countBy(leavers, s => s.site === site.name) };
        }),
        by_region: (['NT', 'SA', 'WA'] as const).map(region => ({
            region,
            active: countBy(active, s => SITES.find(x => x.name === s.site)?.region === region),
        })),
        tenure: {
            average_years: round1(years.reduce((a, b) => a + b, 0) / Math.max(1, years.length)),
            median_years: round1(years[Math.floor(years.length / 2)] ?? 0),
            buckets: [
                { label: 'Under 1 year', count: bucket(0, 1) },
                { label: '1–2 years', count: bucket(1, 2) },
                { label: '2–5 years', count: bucket(2, 5) },
                { label: '5–10 years', count: bucket(5, 10) },
                { label: '10+ years', count: bucket(10, 99) },
            ],
        },
        gender: { female: countBy(active, s => s.gender === 'Female'), male: countBy(active, s => s.gender === 'Male') },
        remote_based: countBy(active, isRemote),
        frontline: countBy(active, isFrontline),
        avg_hourly_rate: avg(active.map(s => s.hourlyRate)),
    };
}

const notDisclosed = (s: Staff) => !s.aboriginal && rng(`people:atsi:${s.id}`).bool(0.12);

function atsiFor(xs: Staff[]) {
    const atsi = countBy(xs, s => s.aboriginal);
    const nd = countBy(xs, notDisclosed);
    return { total_active: xs.length, atsi_count: atsi, non_atsi_count: xs.length - atsi - nd, not_disclosed: nd, atsi_percent: pct1(atsi, xs.length) };
}

// ---------------------------------------------------------------------------
// Leave (sick + other categories), generated per staff per month
// ---------------------------------------------------------------------------

/** 'YYYY-MM' keys from `from` to `to` inclusive. */
function monthRangeKeys(from: string, to: string): string[] {
    const out: string[] = [];
    for (let m = from, guard = 0; m <= to && guard < 40; guard++, m = shiftMonths(`${m}-01`, 1).slice(0, 7)) {
        out.push(m);
    }
    return out;
}

interface LeaveEvent {
    staff: Staff;
    category: string;
    day: string;
    hours: number;
}

const SEASON: Record<number, number> = { 1: 0.8, 2: 0.95, 3: 1, 4: 1, 5: 1.1, 6: 1.35, 7: 1.4, 8: 1.3, 9: 1.05, 10: 0.95, 11: 0.9, 12: 0.8 };

/**
 * Months leave history is generated for: the whole trailing 12 months (EH
 * leave requests aren't limited to the Dec 2025 data window), or from the
 * first selectable quarter (Oct 2025, which payroll also covers) if that is
 * earlier. Each month is seeded on its own, so adding earlier months never
 * changes later ones.
 */
function leaveMonths(): string[] {
    const first = shiftDay(today(), -364).slice(0, 7);
    const firstQuarter = ymd(quarterOf(parseDay(DATA_START_DAY)).start).slice(0, 7);
    return monthRangeKeys(first < firstQuarter ? first : firstQuarter, today().slice(0, 7));
}

function leaveEvents(): LeaveEvent[] {
    return daily('people:leave:events', () => {
        const t = today();
        const out: LeaveEvent[] = [];
        for (const m of leaveMonths()) {
            const mm = Number(m.slice(5));
            const dim = Number(lastDayOfMonth(m).slice(8));
            for (const s of STAFF) {
                if (typeOf(s) === 'casual') {
                    continue;
                }
                const scale = (standardHours(s) ?? 38) / 38;
                const r = rng(`people:leave:${s.id}:${m}`);
                const push = (category: string, hours: number) => {
                    const day = `${m}-${String(r.int(1, dim)).padStart(2, '0')}`;
                    if (day <= t && day >= s.startDate && (!s.endDate || day < s.endDate)) {
                        out.push({ staff: s, category, day, hours: round1(hours * scale) });
                    }
                };
                const pSick = 0.26 * SEASON[mm];
                if (r.next() < pSick) {
                    push("Personal/Carer's Leave", r.pick([7.6, 7.6, 7.6, 15.2, 15.2, 22.8, 38]));
                    if (r.next() < 0.25) {
                        push("Personal/Carer's Leave", r.pick([7.6, 15.2]));
                    }
                }
                if (s.aboriginal && r.next() < 0.07) {
                    push('Cultural & Ceremonial Leave', r.pick([15.2, 22.8, 38]));
                }
                if (tenureDays(s) > 7 * 365 && r.next() < 0.03) {
                    push('Long Service Leave', r.pick([38, 76]));
                }
                if (r.next() < 0.012) {
                    push('Compassionate Leave', r.pick([7.6, 15.2]));
                }
            }
        }
        return out;
    });
}

function sickHoursIn(months: string[]): number {
    return round1(leaveEvents()
        .filter(e => e.category === "Personal/Carer's Leave" && months.includes(e.day.slice(0, 7)))
        .reduce((n, e) => n + e.hours, 0));
}

function sickHoursBetween(from: string, to: string): number {
    return round1(leaveEvents()
        .filter(e => e.category === "Personal/Carer's Leave" && e.day >= from && e.day <= to)
        .reduce((n, e) => n + e.hours, 0));
}

function leaveBlock() {
    const t = today();
    const from = shiftDay(t, -364);
    const events = leaveEvents().filter(e => e.day >= from && e.day <= t);
    const sick = events.filter(e => e.category === "Personal/Carer's Leave");
    const sickHours = round1(sick.reduce((n, e) => n + e.hours, 0));
    // The same 365 days broken into calendar months (first month from `from`),
    // so the rows sum exactly to the 12-month total.
    const trailingRows = monthRangeKeys(from.slice(0, 7), t.slice(0, 7)).map(m => {
        const rowFrom = m === from.slice(0, 7) ? from : `${m}-01`;
        const rowTo = m === t.slice(0, 7) ? t : lastDayOfMonth(m);
        const hours = round1(sick.filter(e => e.day >= rowFrom && e.day <= rowTo).reduce((n, e) => n + e.hours, 0));
        return { month: m, from: rowFrom, to: rowTo, hours, days: round1(hours / 7.6) };
    });
    const pay = payrollCore();
    const annualMonths = pay.monthly.filter(m => m.month >= from.slice(0, 7));
    const months = monthKeys();
    const cq = quarterOf(parseDay(t));
    const pq = quarterOf(parseDay(shiftDay(ymd(cq.start), -1)));
    const quarterSick = (keys: string[]) => {
        const hours = sickHoursIn(keys);
        return { months: keys, hours, days: round1(hours / 7.6) };
    };
    const cats = ["Personal/Carer's Leave", 'Cultural & Ceremonial Leave', 'Long Service Leave', 'Compassionate Leave'].map(category => {
        const xs = events.filter(e => e.category === category);
        const hours = round1(xs.reduce((n, e) => n + e.hours, 0));
        return { category, count: xs.length, hours, days: round1(hours / 7.6) };
    });
    const annualHours = round1(annualMonths.reduce((n, m) => n + m.annual_leave_hours, 0));
    const annualCount = annualMonths.reduce((n, m) => n + m.annual_leave_requests, 0);
    cats.splice(1, 0, { category: 'Annual Leave', count: annualCount, hours: annualHours, days: round1(annualHours / 7.6) });
    return {
        available: true,
        total_requests: cats.reduce((n, c) => n + c.count, 0),
        sick_leave: {
            window: { from, to: t },
            requests: sick.length,
            hours: sickHours,
            days: round1(sickHours / 7.6),
            trailing_12m_by_month: trailingRows,
            monthly_trend: months.map(m => {
                const hours = sickHoursIn([m]);
                return { month: m, hours, days: round1(hours / 7.6) };
            }),
            current_quarter: quarterSick(cq.months),
            previous_quarter: quarterSick(pq.months),
        },
        by_category: cats,
        monthly_trend: months.map(m => {
            const other = leaveEvents()
                .filter(e => e.day.startsWith(m))
                .reduce((n, e) => n + e.hours, 0);
            const annual = pay.monthly.find(p => p.month === m)?.annual_leave_hours ?? 0;
            return { month: m, hours: round1(other + annual) };
        }),
    };
}

// ---------------------------------------------------------------------------
// Payroll — fortnightly pay runs from Oct 2025, built from the roster and reconciled to the ledger
// ---------------------------------------------------------------------------

export const SCHADS_REFERENCE = {
    rate: 38.53,
    template_id: 1,
    template_name: 'SCHADS L2.2',
    fte_dollars_per_month: round2(38.53 * 164.67),
    full_time_hours_per_month: 164.67,
};

/**
 * First pay run. Fortnightly from 2 Oct 2025 (the run cadence that lands on
 * 11 Dec 2025), so every month of every selectable quarter — the first being
 * Oct–Dec 2025 — has its pay runs and reconciles to the ledger.
 */
const PAY_ANCHOR = '2025-10-02';
const REMOTE_ALLOWANCE_PER_HOUR = 2.35;

export function payRunDates(): string[] {
    const out: string[] = [];
    const t = today();
    for (let d = PAY_ANCHOR; d <= t; d = shiftDay(d, 14)) {
        out.push(d);
    }
    return out;
}

interface PayMonth {
    month: string;
    pay_runs: number;
    gross: number;
    super: number;
    payg: number;
    hours: number;
    overtime: number;
    overtime_hours: number;
    penalty: number;
    leave: number;
    leave_hours: number;
    leave_loading: number;
    annual_leave: number;
    annual_leave_hours: number;
    annual_leave_requests: number;
    allowance: number;
    casual: number;
    casual_hours: number;
    by_band: Record<BandKey, number>;
    by_division: Record<string, number>;
    annual_leave_by_division: Record<string, number>;
    annual_leave_hours_by_division: Record<string, number>;
}

function emptyPayMonth(month: string): PayMonth {
    return {
        month,
        pay_runs: 0,
        gross: 0,
        super: 0,
        payg: 0,
        hours: 0,
        overtime: 0,
        overtime_hours: 0,
        penalty: 0,
        leave: 0,
        leave_hours: 0,
        leave_loading: 0,
        annual_leave: 0,
        annual_leave_hours: 0,
        annual_leave_requests: 0,
        allowance: 0,
        casual: 0,
        casual_hours: 0,
        by_band: { dsw: 0, corporate: 0, unknown: 0 },
        by_division: {},
        annual_leave_by_division: {},
        annual_leave_hours_by_division: {},
    };
}

function annualLeaveChance(month: number): number {
    return month === 12 || month === 1 ? 0.2 : month === 7 ? 0.11 : month === 4 || month === 9 || month === 10 ? 0.08 : 0.06;
}

/** One fortnightly pay run, totalled across the employees paid in it. */
interface PayRun {
    id: number;
    period_end: string;
    month: string;
    employees: number;
    gross: number;
    super: number;
    payg: number;
    net: number;
    by_division: Record<string, number>;
}

/**
 * The general ledger's wage and super expense for a month (month-to-date for
 * the current month), which payroll must equal: finance is the source of truth.
 */
function ledgerPayroll(month: string): { wages: number; super: number } | null {
    if (!monthActuals(month)) {
        return null;
    }
    const l = ledgerWages(month, month);
    return { wages: l.wages, super: l.superannuation };
}

/** PAYG withholding rate for a month's pay runs (17.4–19.2% of gross). */
const paygRate = (month: string): number => rng(`people:payg:${month}`).float(0.174, 0.192, 3);

/**
 * Fortnightly pay runs from the roster, reconciled to the ledger. Each run is
 * first built bottom-up per employee (contracted/casual hours × their SCHADS
 * rate, penalties, overtime, remote allowance, annual leave + loading). The
 * ledger's wages for the month are then spread over that month's runs in
 * proportion, which scales the implied hours (rates per level are untouched),
 * so gross pay for any month — and so any quarter or year — equals the
 * ledger's wage accounts and super equals its super accounts.
 */
function payrollCore(): { runs: PayRun[]; monthly: PayMonth[] } {
    return daily('people:payroll:core', () => {
        const dates = payRunDates();
        const months = new Map<string, PayMonth>();
        const raw: { date: string; month: string; employees: number; gross: number; super: number; by_division: Record<string, number> }[] = [];
        for (const run of dates) {
            const m = run.slice(0, 7);
            const pm = months.get(m) ?? emptyPayMonth(m);
            months.set(m, pm);
            pm.pay_runs++;
            const rr = { date: run, month: m, employees: 0, gross: 0, super: 0, by_division: {} as Record<string, number> };
            for (const s of activeOn(run)) {
                const r = rng(`people:pay:${s.id}:${run}`);
                const casual = typeOf(s) === 'casual';
                const weekly = casual ? r.around(typicalHours(s), typicalHours(s) * 0.25, 4, 38, 1) : typicalHours(s);
                const hours = weekly * 2;
                const rate = s.hourlyRate;
                const base = hours * rate * (casual ? 1.25 : 1);
                const penalty = isFrontline(s) ? base * r.float(0.04, 0.13, 3) : 0;
                let otHours = 0;
                if (!casual && r.next() < (isFrontline(s) ? 0.2 : 0.05)) {
                    otHours = r.int(3, 14);
                }
                const overtime = otHours * rate * 1.5;
                const allowance = isRemote(s) ? hours * REMOTE_ALLOWANCE_PER_HOUR : 0;
                let alHours = 0;
                if (!casual && r.next() < annualLeaveChance(Number(m.slice(5)))) {
                    alHours = round1(hours * r.pick([0.25, 0.5, 1]));
                    pm.annual_leave_requests++;
                }
                const annual = alHours * rate;
                const loading = annual * 0.175;
                const gross = base + penalty + overtime + allowance + loading;
                const sup = (gross - overtime) * 0.12;
                pm.gross += gross;
                pm.super += sup;
                pm.hours += hours + otHours;
                pm.overtime += overtime;
                pm.overtime_hours += otHours;
                pm.penalty += penalty;
                pm.allowance += allowance;
                pm.annual_leave += annual;
                pm.annual_leave_hours += alHours;
                pm.leave_loading += loading;
                if (casual) {
                    pm.casual += gross;
                    pm.casual_hours += hours;
                }
                pm.by_band[bandOf(s)] += gross;
                pm.by_division[s.program] = (pm.by_division[s.program] || 0) + gross;
                if (annual > 0) {
                    pm.annual_leave_by_division[s.program] = (pm.annual_leave_by_division[s.program] || 0) + annual;
                    pm.annual_leave_hours_by_division[s.program] = (pm.annual_leave_hours_by_division[s.program] || 0) + alHours;
                }
                rr.employees++;
                rr.gross += gross;
                rr.super += sup;
                rr.by_division[s.program] = (rr.by_division[s.program] || 0) + gross;
            }
            raw.push(rr);
        }

        // Reconcile each month to the ledger: one wage factor for every $ and
        // hours figure, one super factor.
        const wageK = new Map<string, number>();
        const superK = new Map<string, number>();
        for (const pm of months.values()) {
            const ledger = ledgerPayroll(pm.month);
            const k = ledger && pm.gross > 0 ? ledger.wages / pm.gross : 1;
            const ks = ledger && pm.super > 0 ? ledger.super / pm.super : 1;
            wageK.set(pm.month, k);
            superK.set(pm.month, ks);
            const scale = (o: Record<string, number>) => {
                for (const key of Object.keys(o)) {
                    o[key] *= k;
                }
            };
            pm.gross = ledger ? ledger.wages : pm.gross;
            pm.super = ledger ? ledger.super : pm.super;
            pm.hours *= k;
            pm.overtime *= k;
            pm.overtime_hours *= k;
            pm.penalty *= k;
            pm.allowance *= k;
            pm.annual_leave *= k;
            pm.annual_leave_hours *= k;
            pm.leave_loading *= k;
            pm.casual *= k;
            pm.casual_hours *= k;
            scale(pm.by_band);
            scale(pm.by_division);
            scale(pm.annual_leave_by_division);
            scale(pm.annual_leave_hours_by_division);
            // Personal and other leave taken in the month is paid within ordinary hours.
            const other = leaveEvents().filter(e => e.day.startsWith(pm.month));
            pm.leave = pm.annual_leave + other.reduce((n, e) => n + e.hours * e.staff.hourlyRate, 0) * k;
            pm.leave_hours = pm.annual_leave_hours + other.reduce((n, e) => n + e.hours, 0) * k;
            pm.payg = round2(pm.gross * paygRate(pm.month));
        }

        const runs: PayRun[] = raw.map((rr, idx) => {
            const k = wageK.get(rr.month) ?? 1;
            const gross = round2(rr.gross * k);
            const payg = round2(gross * paygRate(rr.month));
            return {
                id: 4101 + idx,
                period_end: rr.date,
                month: rr.month,
                employees: rr.employees,
                gross,
                super: round2(rr.super * (superK.get(rr.month) ?? 1)),
                payg,
                net: round2(gross - payg),
                by_division: Object.fromEntries(Object.entries(rr.by_division).map(([d, v]) => [d, round2(v * k)])),
            };
        });
        return { runs, monthly: [...months.values()].sort((a, b) => a.month.localeCompare(b.month)) };
    });
}

/**
 * FTE figures are paid hours ÷ full-time hours per month (38 h/week), so they
 * can never exceed the people behind them; the SCHADS $ reference is kept for
 * the page note.
 */
function payrollBlock() {
    const core = payrollCore();
    const hrs = SCHADS_REFERENCE.full_time_hours_per_month;
    const roundMap = (o: Record<string, number>) => Object.fromEntries(Object.entries(o).map(([k, v]) => [k, round2(v)]));
    const last = core.runs[core.runs.length - 1];
    return {
        available: core.runs.length > 0,
        source_of_truth: 'Monthly gross equals the MYOB wage accounts (' + WAGE_ACCOUNTS.join(', ') + ') and super equals the super accounts (' + SUPER_ACCOUNTS.join(', ') + ').',
        monthly: core.monthly.map(m => ({
            month: m.month,
            pay_runs: m.pay_runs,
            gross: round2(m.gross),
            super: round2(m.super),
            payg: round2(m.payg),
            net: round2(m.gross - m.payg),
            hours: round1(m.hours),
            overtime: round2(m.overtime),
            overtime_hours: round1(m.overtime_hours),
            penalty: round2(m.penalty),
            leave: round2(m.leave),
            leave_hours: round1(m.leave_hours),
            leave_loading: round2(m.leave_loading),
            annual_leave: round2(m.annual_leave),
            annual_leave_hours: round1(m.annual_leave_hours),
            allowance: round2(m.allowance),
            casual: round2(m.casual),
            casual_hours: round1(m.casual_hours),
            by_band: roundMap(m.by_band) as Record<BandKey, number>,
            overtime_fte: round1(m.overtime_hours / hrs),
            casual_fte: round1(m.casual_hours / hrs),
            leave_fte: round1(m.leave_hours / hrs),
            annual_leave_fte: round1(m.annual_leave_hours / hrs),
            overtime_fte_hours: round1(m.overtime_hours / hrs),
            leave_fte_hours: round1(m.leave_hours / hrs),
            annual_leave_fte_by_division: Object.fromEntries(
                Object.entries(m.annual_leave_hours_by_division)
                    .map(([k, v]) => [k, round2(v / hrs)] as [string, number])
                    .filter(([, v]) => v > 0)
                    .sort((a, b) => b[1] - a[1]),
            ),
        })),
        schads_reference: SCHADS_REFERENCE,
        runs: core.runs.map(({ by_division: _d, ...r }) => r),
        latest_run: last
            ? { id: last.id, period_end: last.period_end, employees: last.employees, gross: last.gross, super: last.super, payg: last.payg, net: last.net, by_division: last.by_division, unmatched_hr_names: [] as string[] }
            : null,
        coverage: { stored_pay_runs: core.runs.length, from: core.runs[0]?.period_end ?? null, to: last?.period_end ?? null },
    };
}

export type PayrollMonth = ReturnType<typeof payrollBlock>['monthly'][number];

// ---------------------------------------------------------------------------
// Recruitment (EH careers feed)
// ---------------------------------------------------------------------------

const TARGET_DAYS = 28;

const POSTINGS: { title: string; department: string; city: string; employment_type: string; days_open: number }[] = [
    { title: 'Disability Support Worker', department: 'Disability Services (NDIS)', city: 'Amata', employment_type: 'Permanent Part Time', days_open: 64 },
    { title: 'Registered Nurse – Aged Care', department: 'Aged Care', city: 'Pukatja (Ernabella)', employment_type: 'Permanent Full Time', days_open: 47 },
    { title: 'Youth Worker', department: 'Youth Services', city: 'Warakurna', employment_type: 'Fixed Term Full Time', days_open: 33 },
    { title: 'DFV Case Manager', department: 'Domestic & Family Violence', city: SITES[0].name, employment_type: 'Permanent Full Time', days_open: 22 },
    { title: 'Child Nutrition Worker', department: 'Child Nutrition', city: 'Kaltjiti (Fregon)', employment_type: 'Casual', days_open: 16 },
    { title: 'Aged Care Worker', department: 'Aged Care', city: 'Mutitjulu', employment_type: 'Casual', days_open: 10 },
    { title: 'Finance Officer', department: 'Corporate Services', city: SITES[0].name, employment_type: 'Permanent Full Time', days_open: 4 },
];

function recruitmentBlock() {
    const t = today();
    const open = POSTINGS.map(p => ({
        ...p,
        created_at: shiftDay(t, -p.days_open),
        overdue: p.days_open > TARGET_DAYS,
        application_url: `https://careers.${ORG.domain}/jobs/${p.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
    })).sort((a, b) => b.days_open - a.days_open);
    const fills = startedBetween(shiftDay(t, -90), t)
        .sort((a, b) => b.startDate.localeCompare(a.startDate))
        .map(s => {
            const days = rng(`people:fill:${s.id}`).int(16, 44);
            return { job_title: s.position, hire_name: s.name, posted_at: shiftDay(s.startDate, -days), started_at: s.startDate, days_to_fill: days, within_target: days <= TARGET_DAYS };
        });
    return {
        available: true,
        target_days: TARGET_DAYS,
        open_count: open.length,
        overdue_count: countBy(open, p => p.overdue),
        avg_days_open: Math.round(open.reduce((n, p) => n + p.days_open, 0) / Math.max(1, open.length)),
        avg_time_to_fill_days: fills.length ? Math.round(fills.reduce((n, f) => n + f.days_to_fill, 0) / fills.length) : null,
        open_postings: open,
        recent_fills: fills,
        caveats: [] as string[],
    };
}

// ---------------------------------------------------------------------------
// /api/eh-data/stats
// ---------------------------------------------------------------------------

function buildEhCore() {
    return {
        connected: true,
        synced: true,
        organisation_name: ORG.name,
        employees: employeesBlock(),
        certifications: { org_types: [] as unknown[], employee_details: [] as unknown[], fetch_failed: [] as unknown[] },
        turnover: turnoverBlock(),
        attrition_schads: attritionBlock(),
        fte: fteBlock(),
        classification: classificationBlock(),
        workforce: workforceBlock(),
        atsi: atsiFor(activeToday()),
        overtime: { available: false },
        leave: leaveBlock(),
        recruitment: recruitmentBlock(),
        payroll: payrollBlock(),
        warnings: [] as { code: string; message: string; action?: string }[],
    };
}

export type EhStats = ReturnType<typeof buildEhCore> & { last_synced_at: string; compliance_by_category: { categories: any[] } };

/** GET /api/eh-data/stats — the synced Employment Hero payload. Board gets the BoardProjection allowlist. */
export function ehStats(board = false): EhStats | Record<string, unknown> {
    const core = daily('people:eh:core', buildEhCore);
    const last = lastSyncedAt('employment_hero');
    if (!board) {
        return { ...core, last_synced_at: last, compliance_by_category: complianceByCategory(false) };
    }
    const { recent_fills: _fills, ...recruitment } = core.recruitment;
    return {
        connected: core.connected,
        synced: core.synced,
        last_synced_at: last,
        employees: { total_active: core.employees.total_active, by_type: core.employees.by_type },
        compliance_by_category: complianceByCategory(true),
        turnover: core.turnover,
        attrition_schads: core.attrition_schads,
        atsi: core.atsi,
        leave: {
            available: core.leave.available,
            sick_leave: {
                window: core.leave.sick_leave.window,
                days: core.leave.sick_leave.days,
                hours: core.leave.sick_leave.hours,
                monthly_trend: core.leave.sick_leave.monthly_trend,
                trailing_12m_by_month: core.leave.sick_leave.trailing_12m_by_month,
            },
        },
        recruitment: { ...recruitment, open_postings: recruitment.open_postings.map(({ application_url: _u, ...p }) => p) },
        payroll: {
            available: core.payroll.available,
            schads_reference: {
                rate: core.payroll.schads_reference.rate,
                fte_dollars_per_month: core.payroll.schads_reference.fte_dollars_per_month,
                full_time_hours_per_month: core.payroll.schads_reference.full_time_hours_per_month,
            },
            monthly: core.payroll.monthly,
        },
    };
}

/** Full (non-board) EH stats, typed, for other generators. */
export function ehSummary(): EhStats {
    return ehStats(false) as EhStats;
}

export const EH_NOT_CONNECTED = { connected: false, message: 'Employment Hero is not connected. Please authorize via Settings > Integrations.' };

// ---------------------------------------------------------------------------
// /api/trends/employment_hero — daily history computed from the roster
// ---------------------------------------------------------------------------

export function ehTrendRow(day: string): Record<string, unknown> {
    const active = activeOn(day);
    const bt = byType(active);
    const from = shiftDay(day, -364);
    const hired = byType(startedBetween(from, day));
    const left = byType(endedBetween(from, day));
    const atsi = atsiFor(active);
    const sum = (o: ByType<number>) => TYPE_KEYS.reduce((n, k) => n + o[k], 0);
    return {
        date: day,
        total_active: active.length,
        total_terminated: countBy(STAFF, s => !!s.endDate && s.endDate <= day),
        total: countBy(STAFF, s => s.startDate <= day),
        active_fulltime: bt.fulltime,
        active_parttime: bt.parttime,
        active_casual: bt.casual,
        active_other: bt.other + bt.unknown,
        hired_12m: sum(hired),
        hired_12m_fulltime: hired.fulltime,
        hired_12m_parttime: hired.parttime,
        hired_12m_casual: hired.casual,
        terminated_12m: sum(left),
        terminated_12m_fulltime: left.fulltime,
        terminated_12m_parttime: left.parttime,
        terminated_12m_casual: left.casual,
        turnover_rate_12m: turnoverRate12m(day),
        turnover_rate_12m_fulltime: turnoverRate12m(day, 'fulltime'),
        turnover_rate_12m_parttime: turnoverRate12m(day, 'parttime'),
        turnover_rate_12m_casual: turnoverRate12m(day, 'casual'),
        atsi_percent: atsi.atsi_percent,
        atsi_count: atsi.atsi_count,
        recruitment_open: POSTINGS.filter(p => shiftDay(today(), -p.days_open) <= day).length,
    };
}

const BOARD_TREND_FIELDS = ['date', 'total_active', 'active_fulltime', 'active_parttime', 'active_casual', 'turnover_rate_12m', 'turnover_rate_12m_fulltime', 'turnover_rate_12m_parttime', 'turnover_rate_12m_casual'];

/** GET /api/trends/employment_hero?quarter=… | ?days=N (board: `{data}` only, allowlisted fields). */
export function ehTrends(q: Quarter | null, daysParam: string | null = null, board = false): Record<string, unknown> {
    const w = trendWindow(q, daysParam);
    const data = w.dates.map(d => daily(`people:eh:trend:${d}`, () => ehTrendRow(d)));
    if (board) {
        return { data: data.map(row => Object.fromEntries(BOARD_TREND_FIELDS.map(k => [k, row[k]]))) };
    }
    return { source: 'employment_hero', days: w.days, quarter: w.quarter, range: w.range, count: data.length, data };
}

// ---------------------------------------------------------------------------
// Cross-surface summary (board dashboard, CEO quarterly report, assistant)
// ---------------------------------------------------------------------------

/**
 * People headline figures for a quarter (or All Time = since Dec 2025).
 * Point-in-time fields (active_staff, turnover_rate_12m, training_*,
 * compliance) are CURRENT values, exactly as the dashboard tiles show them;
 * *_at_period_end fields (headcount, ATSI share, 12-month turnover) are the
 * roster as at the quarter's last day — today, for the current quarter — and
 * *_in_period fields cover the quarter (to date, if it is the current one).
 */
export function peopleSummary(q: Quarter | null) {
    const t = today();
    const from = q ? ymd(q.start) : DATA_START_DAY;
    const qEnd = q ? ymd(q.end) : t;
    const to = qEnd < t ? qEnd : t;
    const eh = ehSummary();
    const training = trainingSummary();
    const compliance = complianceSummary();
    const pay = eh.payroll.monthly.filter(m => m.month >= from.slice(0, 7) && m.month <= to.slice(0, 7));
    const sumPay = (k: 'gross' | 'super' | 'payg' | 'net' | 'overtime' | 'casual' | 'annual_leave' | 'penalty' | 'allowance') => round2(pay.reduce((n, m) => n + m[k], 0));
    const hires = from <= to ? startedBetween(from, to) : [];
    const leavers = from <= to ? endedBetween(from, to) : [];
    const endActive = activeOn(to);
    const sick = sickHoursBetween(from, to);
    return {
        quarter: q ? q.key : null,
        period: { from, to },
        // Board dashboard `people` block (current, not quarter-scoped)
        active_staff: eh.employees.total_active,
        training_completion_rate: training.completion_rate,
        training_in_progress: training.in_progress,
        turnover_rate_12m: eh.turnover.turnover_rate_12m,
        headcount_by_type: eh.employees.by_type,
        total_fte: eh.fte.total_fte,
        atsi_percent: eh.atsi.atsi_percent,
        // Quarter-scoped movement
        headcount_at_period_end: endActive.length,
        headcount_at_period_end_by_type: byType(endActive),
        atsi_at_period_end: atsiFor(endActive),
        turnover_at_period_end: turnover12m(to),
        hires_in_period: hires.length,
        terminations_in_period: leavers.length,
        hires_in_period_by_type: byType(hires),
        terminations_in_period_by_type: byType(leavers),
        termination_reasons_in_period: Object.entries(
            leavers.reduce<Record<string, number>>((a, s) => ((a[s.terminationReason || 'Not recorded'] = (a[s.terminationReason || 'Not recorded'] || 0) + 1), a), {}),
        ).map(([reason, count]) => ({ reason, count })),
        sick_leave_hours_in_period: sick,
        sick_leave_days_in_period: round1(sick / 7.6),
        payroll_in_period: {
            months: pay.map(m => m.month),
            pay_runs: pay.reduce((n, m) => n + m.pay_runs, 0),
            gross: sumPay('gross'),
            super: sumPay('super'),
            payg: sumPay('payg'),
            net: sumPay('net'),
            overtime: sumPay('overtime'),
            casual: sumPay('casual'),
            annual_leave: sumPay('annual_leave'),
            penalty: sumPay('penalty'),
            allowance: sumPay('allowance'),
        },
        // Current point-in-time quality measures
        training: {
            completion_rate: training.completion_rate,
            in_progress: training.in_progress,
            staff_tracked: training.staff_count,
            expired: training.expired,
            expiring_soon: training.expiring_soon,
            untracked_staff: training.untracked_staff.length,
        },
        compliance,
        attrition_schads: {
            dsw_rate: eh.attrition_schads.turnover_rate_12m_by_band.dsw,
            corporate_rate: eh.attrition_schads.turnover_rate_12m_by_band.corporate,
            active_dsw: eh.attrition_schads.active_headcount_by_band.dsw,
            active_corporate: eh.attrition_schads.active_headcount_by_band.corporate,
        },
        recruitment: {
            open: eh.recruitment.open_count,
            overdue: eh.recruitment.overdue_count,
            avg_days_open: eh.recruitment.avg_days_open,
            avg_time_to_fill_days: eh.recruitment.avg_time_to_fill_days,
            target_days: eh.recruitment.target_days,
        },
    };
}

/** Staff employed at some point in the quarter — denominator helper for other areas. */
export function headcountInQuarter(q: Quarter): number {
    return STAFF.filter(s => s.startDate <= ymd(q.end) && (!s.endDate || s.endDate >= ymd(q.start))).length;
}
